import { useQueries, UseQueryResult } from '@tanstack/react-query';

import { ensureAgentHubBrands } from '~/consts/agentHub';

import useCurrentBrand from '~/hooks/useCurrentBrand';

import { fetchAgentHubUserById } from '~/services/AgentHub/AgentService';

import type { Agent } from '~/types/services/agentHub';

export const agentHubUserDetailQueryKeys = {
  all: ['agent-hub-user-detail'] as const,
  detail: (id: string, brand: string) => [...agentHubUserDetailQueryKeys.all, id, brand] as const,
};

interface UseAgentHubUserRowsDetailQueriesOptions {
  enabled?: boolean;
}

const FIVE_MINUTES = 5 * 60 * 1000;

export function useAgentHubUserRowsDetailQueries(
  rows: Array<{ id: string }>,
  options: UseAgentHubUserRowsDetailQueriesOptions = {},
): {
  queryByRowId: Map<string, UseQueryResult<Agent>>;
  isDetailPending: boolean;
} {
  const { enabled = true } = options;
  const brand = useCurrentBrand();
  const resolvedBrand = ensureAgentHubBrands(brand);

  const queries = useQueries({
    queries: rows.map((row) => ({
      queryKey: agentHubUserDetailQueryKeys.detail(row.id, resolvedBrand),
      queryFn: async () => {
        return await fetchAgentHubUserById(row.id);
      },
      enabled: enabled && !!row.id,
      staleTime: FIVE_MINUTES,
    })),
  });

  const queryByRowId = new Map<string, UseQueryResult<Agent>>();
  rows.forEach((row, index) => {
    queryByRowId.set(row.id, queries[index]);
  });

  const isDetailPending = queries.some((q) => q.isPending);

  return { queryByRowId, isDetailPending };
}
